import { apiClient } from "@/services/api.client"

import type { CitationReview } from "../lib/review"

/**
 * Officer citation review — frontend client for the nitiassist proxy
 * (/api/v1/precedents/*), which authenticates the officer, injects their
 * identity server-side, and forwards to oio_service's precedent endpoints
 * (the precedent agent's own IndianKanoon client + deterministic verifier).
 *
 * Privacy: only the citation / case-name / court strings ever leave here.
 */

export interface PrecedentCandidate {
  source: string
  doc_id: string
  url: string
  title: string
  court: string
  year: number | null
  neutral_citation: string
  snippet: string
  treatment: string
}

export interface PrecedentVerdict {
  verification_status: string
  name_score: number
  court_match: boolean
  year_match: boolean
  reason: string
  record: PrecedentCandidate
}

export interface ResolveResponse {
  status: string
  action: string
  unverified_citation_count: number
  citation_reviews: CitationReview[]
  review_report_blob_url: string
}

export async function searchPrecedents(params: {
  raw_citation?: string
  case_name?: string
  court?: string
  limit?: number
}): Promise<PrecedentCandidate[]> {
  const { candidates } = await apiClient.post<{ candidates: PrecedentCandidate[] }>(
    "/api/v1/precedents/search",
    params,
  )
  return candidates ?? []
}

export async function verifyPrecedent(params: {
  raw_citation?: string
  case_name?: string
  court?: string
  doc_id: string
  source?: string
}): Promise<PrecedentVerdict> {
  return apiClient.post<PrecedentVerdict>("/api/v1/precedents/verify", params)
}

export async function resolvePrecedent(params: {
  thread_id: string
  allegation_id: string
  action: "confirm" | "discard" | "add"
  raw_citation?: string
  case_name?: string
  court?: string
  doc_id?: string
  source?: string
}): Promise<ResolveResponse> {
  return apiClient.post<ResolveResponse>("/api/v1/precedents/resolve", params)
}
